import { fmtLabel, fmtNum, plural3, isRest, kgText, mmss, type Block, type ComplexFormat, type FormatKind, type Maxes } from '@tg/domain';
import type { Catalog } from '@tg/app-core';
import { useEffect, useRef, useState } from 'react';
import { num, planOf, segments, type LinePlan, type SetFact } from '../lib/workout';
import { I } from './icons';

/* ═══ Вкладка «Итог» блока (прототип: screens.js clResEntries / clResTab) ═══
   Комплекс — один результат на весь комплекс: поля с единицами справа, кнопка «Записать».
   Силовой блок — по подходам каждого упражнения (наше дополнение): № · вес · повторы · отметка,
   «+ Подход»; правка подхода сохраняется сама. «Записать» засчитывает только подходы с отметкой ✓ или
   с вписанным весом/повторами (пустое поле такого подхода — план); нетронутые подходы остаются не сделанными —
   тренер видит, что сделано на самом деле
   из подсказки. Тап по строке упражнения открывает «Итог» на ней.
   Круги (суперсет, круговая) — по кругам: «Круг 1» — все упражнения круга подряд, отметка круга целиком, «+ Круг»;
   хранится так же по упражнениям (подход № k — круг k), тренер видит те же подходы.
   Факт в results: строка — { sets: [{ kg, reps, done }] } (раньше бывал { kg }), комплекс — { rounds, reps } / { time }. */

export type Fact = Record<string, unknown>;
type Field = [key: string, unit: string];
interface FmtSpec {
  f: Field[];
  /** Поля → факт для сервера */
  out: (v: Record<string, number>) => Fact;
  /** Факт → значения полей (записанное раньше) */
  inp: (x: Fact) => Record<string, number | null | undefined>;
  /** Записанное — справа от строки */
  val: (x: Fact, f: ComplexFormat) => string;
}
const ofN = (u: string) => ({ f: [['r', u]] as Field[], out: (v: Record<string, number>) => ({ rounds: v.r }), inp: (x: Fact) => ({ r: x.rounds as number }) });
const FMT: Partial<Record<FormatKind, FmtSpec>> = {
  AMRAP: { f: [['r', 'раундов'], ['p', 'повторов']], out: (v) => ({ rounds: v.r ?? 0, reps: v.p ?? 0 }), inp: (x) => ({ r: x.rounds as number, p: x.reps as number }), val: (x) => `${x.rounds ?? 0} + ${x.reps ?? 0}` },
  FOR_TIME: { f: [['m', 'мин'], ['s', 'сек']], out: (v) => ({ time: (v.m ?? 0) * 60 + (v.s ?? 0) }), inp: (x) => (x.time != null ? { m: Math.floor((x.time as number) / 60), s: Math.round(x.time as number) % 60 } : {}), val: (x) => mmss((x.time as number) ?? 0) },
  EMOM: { ...ofN('из %n раундов'), val: (x, f) => `${x.rounds ?? 0} из ${f.rounds}` },
  DEATH_BY: { ...ofN('раундов'), val: (x) => `${x.rounds ?? 0} раундов` },
  TABATA: { f: [['r', 'повторов всего']], out: (v) => ({ reps: v.r }), inp: (x) => ({ r: x.reps as number }), val: (x) => `${x.reps ?? 0} повт.` },
  INTERVALS: { ...ofN('из %n интервалов'), val: (x, f) => `${x.rounds ?? 0} из ${f.rounds}` },
  ROUNDS: { ...ofN('из %n раундов'), val: (x, f) => `${x.rounds ?? 0} из ${f.rounds}` },
};

export interface ResEntry {
  lineId: string;
  exId: string | null;
  label: string;
  fmt?: ComplexFormat;
  plan?: LinePlan;
  /** Упражнение в кругах: id круга (первая строка) и число кругов */
  grp?: { id: string; rounds: number };
}

/** Что в блоке записывается: формат комплекса или каждое упражнение с подходами (и в кругах, и связки; не отдых) */
export function resEntries(b: Block, pm: Maxes | null, cat: Catalog): ResEntry[] {
  if (b.fmt) return FMT[b.fmt.k] ? [{ lineId: '', exId: null, label: fmtLabel(b.fmt), fmt: b.fmt }] : [];
  const out: ResEntry[] = [];
  for (const seg of segments(b)) {
    const first = seg.find((r) => r.k === 'line');
    const grp = seg[0]?.k === 'head' && first?.k === 'line' && first.rounds > 1 ? { id: first.line.id, rounds: first.rounds } : undefined;
    for (const r of seg) {
      if (r.k !== 'line' || isRest(r.line)) continue;
      const plan = planOf(r.line, r.rounds, pm, cat.ex);
      if (!plan.sets) continue;
      /* «5×3 Приседания со штангой» — вес в поле подсказкой */
      out.push({
        lineId: r.line.id,
        exId: r.line.t === 'ex' ? r.line.exId : null,
        label: plan.text.replace(/\s*[\d,.]+(?:\s*[–-]\s*[\d,.]+)?\s*кг\s*$/, ''),
        plan,
        grp,
      });
    }
  }
  return out;
}

/** Записанный вес строки: новое — { kg }, раньше записывали по подходам — наибольший сделанный */
const kgOf = (x: Fact | null | undefined): number | null => {
  if (!x) return null;
  if (typeof x.kg === 'number') return x.kg;
  const kgs = ((x.sets as SetFact[] | undefined) || []).filter((s) => s.done && s.kg != null).map((s) => s.kg as number);
  return kgs.length ? Math.max(...kgs) : null;
};
const planKg = (p?: LinePlan) => (p?.kg && p.kg.hi == null ? p.kg.lo : null);

/** Значение справа от строки: «7 + 12», «8:45», «3 из 3 подх.», «120 кг» (записано раньше одним весом) */
export function resValue(e: ResEntry, x: Fact | null | undefined): string {
  if (!x) return '';
  if (e.fmt) {
    const s = FMT[e.fmt.k];
    const v = s?.inp(x) || {};
    return s && Object.values(v).some((n) => n != null) ? s.val(x, e.fmt) : '';
  }
  const sets = (x.sets as SetFact[] | undefined) || [];
  if (sets.length) {
    const n = sets.filter((s) => s.done).length;
    return n ? `${n} из ${Math.max(e.plan?.sets || 0, sets.length)} подх.` : '';
  }
  const kg = kgOf(x);
  return kg == null ? '' : fmtNum(kg) + ' кг' + (kg === planKg(e.plan) ? ' ✓' : '');
}

/* ── Подходы упражнения: № · вес · повторы · отметка ── */
type Row = { kg: string; reps: string; done: boolean };
const str = (v: unknown) => (v == null || v === '' ? '' : fmtNum(v as number));
const loKg = (p: LinePlan) => (p.kg ? p.kg.lo : null);

function rowsOf(plan: LinePlan, fact: Fact | null): Row[] {
  const sets = (fact?.sets as SetFact[] | undefined) || [];
  /* Записано раньше одним весом на строку — он во всех подходах */
  const one = !sets.length && typeof fact?.kg === 'number' ? str(fact.kg) : '';
  const n = Math.max(plan.sets, sets.length);
  return Array.from({ length: n }, (_, i) => ({
    kg: sets[i] ? str(sets[i].kg) : one,
    reps: str(sets[i]?.reps),
    done: sets[i] ? !!sets[i].done : !!one,
  }));
}

/** Подход тронут: отмечен ✓ или вписан вес/повторы */
const touched = (r: Row) => r.done || !!r.kg.trim() || !!r.reps.trim();

/** Подходы → факт; mark — «Записать»: тронутые подходы сделаны, пустое поле в них — план */
function factOfRows(plan: LinePlan, rs: Row[], mark = false): Fact {
  return {
    sets: rs.map((r) => {
      const done = r.done || (mark && touched(r));
      return {
        kg: plan.weighted ? (num(r.kg) ?? (done ? loKg(plan) : null)) : null,
        reps: num(r.reps) ?? (done && !plan.chain ? num(plan.reps) : null),
        done,
      };
    }),
    ...(plan.unit !== 'повт' ? { unit: plan.unit } : {}),
  };
}

const EMPTY: Row = { kg: '', reps: '', done: false };

/** Один подход: № · вес · повторы · отметка. Подсказка в поле веса — план; без плана пусто (единица и так справа) */
function SetRow({ plan, r, n, label, set }: { plan: LinePlan; r: Row; n: string; label: string; set: (p: Partial<Row>, now?: boolean) => void }) {
  const kgPh = plan.kg ? kgText(plan.kg)! : '';
  const repsPh = plan.chain ? plan.reps : plan.reps || (plan.unit === 'повт' ? 'повт' : plan.unit);
  return (
    <div className={'sr' + (r.done ? ' ok' : '') + (plan.weighted ? '' : ' nokg')}>
      <span className="sn">{n}</span>
      {plan.weighted ? (
        <label className="rf">
          <input inputMode="decimal" placeholder={kgPh} value={r.kg} onChange={(e) => set({ kg: e.target.value })} aria-label={`${label}, вес`} />
          <i>кг</i>
        </label>
      ) : null}
      {plan.chain ? (
        <span className="rfx">{plan.reps}</span>
      ) : (
        <label className="rf">
          <input inputMode="decimal" placeholder={repsPh} value={r.reps} onChange={(e) => set({ reps: e.target.value })} aria-label={`${label}, ${plan.unit}`} />
          <i>{plan.unit}</i>
        </label>
      )}
      <button className={'rck' + (r.done ? ' on' : '')} onClick={() => set({ done: !r.done }, true)} aria-label={r.done ? 'Снять отметку подхода' : 'Подход сделан'}>
        {r.done ? <I n="check" /> : null}
      </button>
    </div>
  );
}

function SetRows({ plan, rows, onRows }: { plan: LinePlan; rows: Row[]; onRows: (rs: Row[], now?: boolean) => void }) {
  const set = (i: number, p: Partial<Row>, now = false) =>
    onRows(
      rows.map((r, k) => (k === i ? { ...r, ...p } : r)),
      now,
    );
  return (
    <div className="res">
      {rows.map((r, i) => (
        <SetRow key={i} plan={plan} r={r} n={String(i + 1)} label={`Подход ${i + 1}`} set={(p, now) => set(i, p, now)} />
      ))}
      <button className="radd" onClick={() => onRows([...rows, { kg: rows[rows.length - 1]?.kg || '', reps: '', done: false }])}>
        <I n="plus" />
        Подход
      </button>
    </div>
  );
}

/** Название упражнения без схемы впереди: «8-12 Румынская тяга» → «Румынская тяга» (повторы — в поле) */
const exName = (label: string) => label.replace(/^[\d\s×xх*,.\/+–-]+(?=[а-яёa-z(])/i, '') || label;

/* Круги: подход № k·per … (k+1)·per−1 каждого упражнения — круг k. per — подходов упражнения в одном круге */
function RoundRows({ es, rounds, rows, onRows }: { es: ResEntry[]; rounds: number; rows: Record<string, Row[]>; onRows: (e: ResEntry, rs: Row[], now?: boolean) => void }) {
  const per = (e: ResEntry) => Math.max(1, Math.round(e.plan!.sets / rounds));
  const n = Math.max(rounds, ...es.map((e) => Math.ceil((rows[e.lineId] || []).length / per(e))));
  const rowAt = (e: ResEntry, i: number) => (rows[e.lineId] || [])[i] || EMPTY;
  const put = (e: ResEntry, i: number, p: Partial<Row>, now = false) => {
    const rs = [...(rows[e.lineId] || [])];
    while (rs.length <= i) rs.push({ ...EMPTY });
    rs[i] = { ...rs[i], ...p };
    onRows(e, rs, now);
  };
  const idx = (e: ResEntry, k: number) => Array.from({ length: per(e) }, (_, j) => k * per(e) + j);
  const roundDone = (k: number) => es.every((e) => idx(e, k).every((i) => rowAt(e, i).done));
  const toggle = (k: number) => {
    const on = !roundDone(k);
    for (const e of es) {
      const rs = [...(rows[e.lineId] || [])];
      for (const i of idx(e, k)) {
        while (rs.length <= i) rs.push({ ...EMPTY });
        rs[i] = { ...rs[i], done: on };
      }
      onRows(e, rs, true);
    }
  };
  return (
    <>
      {Array.from({ length: n }, (_, k) => (
        <div className={'res rround' + (roundDone(k) ? ' ok' : '')} key={k}>
          <div className="rrh">
            <b>Круг {k + 1}</b>
            <button className={'rck' + (roundDone(k) ? ' on' : '')} onClick={() => toggle(k)} aria-label={roundDone(k) ? `Снять отметку круга ${k + 1}` : `Круг ${k + 1} сделан`}>
              {roundDone(k) ? <I n="check" /> : null}
            </button>
          </div>
          {es.map((e) =>
            idx(e, k).map((i, j) => (
              <div className="rrx" key={e.lineId + ':' + i}>
                <span className="rrn">
                  {exName(e.label)}
                  {per(e) > 1 ? ` · подход ${j + 1}` : ''}
                </span>
                <SetRow plan={e.plan!} r={rowAt(e, i)} n="" label={`Круг ${k + 1}, ${exName(e.label)}`} set={(p, now) => put(e, i, p, now)} />
              </div>
            )),
          )}
        </div>
      ))}
      <button
        className="radd"
        onClick={() => {
          for (const e of es) {
            const rs = [...(rows[e.lineId] || [])];
            while (rs.length < (n + 1) * per(e)) rs.push({ ...EMPTY, kg: rs[rs.length - per(e)]?.kg || '' });
            onRows(e, rs);
          }
        }}
      >
        <I n="plus" />
        Круг
      </button>
    </>
  );
}

/** Сделано кругов: круг засчитан, когда отмечены все его подходы */
function roundsDone(es: ResEntry[], rounds: number, factOf: (lineId: string) => Fact | null) {
  const sets = es.map((e) => ((factOf(e.lineId)?.sets as SetFact[] | undefined) || []) as SetFact[]);
  const per = es.map((e) => Math.max(1, Math.round(e.plan!.sets / rounds)));
  const n = Math.max(rounds, ...sets.map((s, x) => Math.ceil(s.length / per[x])));
  let done = 0;
  for (let k = 0; k < n; k++) if (es.every((_, x) => Array.from({ length: per[x] }, (_, j) => sets[x][k * per[x] + j]?.done).every(Boolean))) done++;
  return { done, n };
}

export function ResTab({
  ents,
  factOf,
  onSave,
  onAuto,
  focus,
}: {
  ents: ResEntry[];
  factOf: (lineId: string) => Fact | null;
  /** «Записать» */
  onSave: (list: { e: ResEntry; fact: Fact }[]) => void;
  /** Правка подхода — сохраняется сама, без тоста */
  onAuto: (e: ResEntry, fact: Fact) => void;
  /** Тап по строке упражнения — «Итог» открывается на ней */
  focus?: { id: string; n: number } | null;
}) {
  const strength = !ents[0]?.fmt;
  const [rows, setRows] = useState<Record<string, Row[]>>(() => Object.fromEntries(ents.filter((e) => e.plan).map((e) => [e.lineId, rowsOf(e.plan!, factOf(e.lineId))])));
  /* Правки подходов уходят через 0,7 с; закрыли вкладку — сразу */
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pend = useRef<Record<string, () => void>>({});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => Object.values(pend.current).forEach((f) => f()), []);
  const onRows = (e: ResEntry, rs: Row[], now = false) => {
    setRows((x) => ({ ...x, [e.lineId]: rs }));
    clearTimeout(timers.current[e.lineId]);
    const run = () => {
      delete pend.current[e.lineId];
      onAuto(e, factOfRows(e.plan!, rs));
    };
    pend.current[e.lineId] = run;
    if (now) run();
    else timers.current[e.lineId] = setTimeout(run, 700);
  };
  useEffect(() => {
    if (!focus) return;
    let el = document.getElementById('re-' + focus.id);
    /* Упражнение в кругах — метка внутри блока кругов */
    if (el?.hidden) el = el.parentElement;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('qflash');
    const t = setTimeout(() => el.classList.remove('qflash'), 1600);
    return () => clearTimeout(t);
  }, [focus]);

  const fieldsOf = (e: ResEntry): Field[] => (e.fmt ? FMT[e.fmt.k]!.f.map(([k, u]) => [k, u.replace('%n', String(e.fmt!.rounds || ''))] as Field) : [['w', 'кг']]);
  const init = () => {
    const o: Record<string, string> = {};
    for (const e of ents) {
      const x = factOf(e.lineId);
      const v: Record<string, number | null | undefined> = e.fmt ? (x ? FMT[e.fmt.k]!.inp(x) : {}) : { w: kgOf(x) };
      for (const [k] of fieldsOf(e)) o[e.lineId + ':' + k] = v[k] != null ? fmtNum(v[k] as number) : '';
    }
    return o;
  };
  const [val, setVal] = useState(init);
  /* «Записать» без единого отмеченного подхода — подсказка */
  const [need, setNeed] = useState(false);
  const first = ents[0] ? ents[0].lineId + ':' + fieldsOf(ents[0])[0][0] : '';

  const save = () => {
    if (strength) {
      for (const e of ents) {
        clearTimeout(timers.current[e.lineId]);
        delete pend.current[e.lineId];
      }
      /* Упражнения без единого тронутого подхода не записываются — сделано ровно то, что отметил клиент */
      const list = ents.filter((e) => (rows[e.lineId] || []).some(touched)).map((e) => ({ e, fact: factOfRows(e.plan!, rows[e.lineId] || [], true) }));
      if (!list.length) {
        setNeed(true);
        return;
      }
      setNeed(false);
      /* В полях — то, что записано (план из подсказки становится значением) */
      setRows(Object.fromEntries(list.map(({ e, fact }) => [e.lineId, rowsOf(e.plan!, fact)])));
      onSave(list);
      return;
    }
    const list: { e: ResEntry; fact: Fact }[] = [];
    for (const e of ents) {
      const v: Record<string, number> = {};
      for (const [k] of fieldsOf(e)) {
        /* Пустое поле — план из подсказки; диапазон «127,5–135» — не число, вес вводит клиент */
        const raw = (val[e.lineId + ':' + k] || (e.fmt ? '' : (kgText(e.plan?.kg ?? null) ?? ''))).trim();
        const n = raw ? num(raw) : null;
        if (n != null && isFinite(n)) v[k] = n;
      }
      if (!Object.keys(v).length) continue;
      list.push({ e, fact: e.fmt ? FMT[e.fmt.k]!.out(v) : { kg: v.w } });
    }
    if (!list.length) {
      document.getElementById('rf-' + first)?.focus();
      return;
    }
    onSave(list);
  };

  /* Подряд идущие упражнения одного круга — одним блоком «Круг 1, Круг 2…» */
  const units: (ResEntry | ResEntry[])[] = [];
  for (const e of ents) {
    const last = units[units.length - 1];
    if (e.grp && Array.isArray(last) && last[0].grp?.id === e.grp.id) last.push(e);
    else units.push(e.grp && e.plan ? [e] : e);
  }

  return (
    <div className="rb">
      {units.map((u) => {
        if (Array.isArray(u)) {
          const g = u[0].grp!;
          const rd = roundsDone(u, g.rounds, factOf);
          return (
            <div className="rent" key={'g' + g.id} id={'re-' + g.id}>
              {u.map((e) => (e.lineId !== g.id ? <span key={e.lineId} id={'re-' + e.lineId} hidden /> : null))}
              <div className="rl">
                <span>
                  {g.rounds} {plural3(g.rounds, 'круг', 'круга', 'кругов')}
                </span>
                {rd.done ? (
                  <s>
                    <I n="check" />
                    {rd.done} из {rd.n} кругов
                  </s>
                ) : null}
              </div>
              <RoundRows es={u} rounds={g.rounds} rows={rows} onRows={onRows} />
            </div>
          );
        }
        const e = u;
        const shown = resValue(e, factOf(e.lineId));
        const ph = e.fmt ? '' : (kgText(e.plan?.kg ?? null) ?? '');
        return (
          <div className="rent" key={e.lineId || 'fmt'} id={'re-' + e.lineId}>
            <div className="rl">
              <span>{e.label}</span>
              {shown ? (
                <s>
                  <I n="check" />
                  {shown}
                </s>
              ) : null}
            </div>
            {e.plan ? (
              <SetRows plan={e.plan} rows={rows[e.lineId] || []} onRows={(rs, now) => onRows(e, rs, now)} />
            ) : (
              <div className="rflds">
                {fieldsOf(e).map(([k, u]) => {
                  const id = e.lineId + ':' + k;
                  return (
                    <label className="rfld" key={k}>
                      <input
                        id={'rf-' + id}
                        inputMode="decimal"
                        value={val[id] ?? ''}
                        placeholder={ph}
                        aria-label={u}
                        onChange={(x) => setVal((s) => ({ ...s, [id]: x.target.value }))}
                        onKeyDown={(x) => x.key === 'Enter' && save()}
                      />
                      <i>{u}</i>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {strength ? <p className={'rhint' + (need ? ' on' : '')}>Отметьте ✓ сделанные подходы или впишите вес и повторы — тренер увидит только их</p> : null}
      <button className="btn pri rsave" onClick={save}>
        Записать
      </button>
    </div>
  );
}
